import { ORIGIN_VIEWPORT, PREPARER, ZoomContextShape } from 'hellospa/components/signature-request';
import { getMinFieldDimensions } from '../constants';
import { moveInitialOffset } from './move-field';
import { Field, FieldTypes } from '../types';
import { EditorContextShape } from '../context/editor-context';
import { XYCoord } from 'react-dnd';

export function applyFieldConstraints<T extends Field>(fieldData: T): T {

  if (fieldData.type === FieldTypes.Text && fieldData.signer !== PREPARER) {
    // @ts-ignore I don't know why TypeScript isn't figuring out that this is a
    // TextField. I think it has to do with `T extends Field`
    const lineHeight = fieldData.lineHeight;
    const height = Math.round(Math.max(
      lineHeight,
      Math.round(fieldData.height / lineHeight) * lineHeight,
    ));

    // Text fields must always fit a whole number of lines
    if (height !== fieldData.height) {
      return Object.assign({}, fieldData, { height });
    }
  } else if (fieldData.type === 'checkbox' || fieldData.type === 'radiobutton') {
    // Checkboxes and radio buttons must be square
    const size = Math.min(fieldData.width, fieldData.height);

    if (
      fieldData.width !== size
      || fieldData.height !== size
    ) {
      return Object.assign({}, fieldData, {
        width: size,
        height: size,
      });
    }
  }

  return fieldData;
}


export type Direction = 'N' | 'S' | 'E' | 'W'

const nudgeImpl = <T extends Field>(
  fieldData: T,
  direction: Direction,
  resize: boolean,
  distance = 1,
): T => {
  const {
    x, y, height, width,
  } = fieldData;

  const newFieldData = Object.assign({}, fieldData);
  switch (direction) {
    case 'N': Object.assign(newFieldData, resize
      ? { height: height - distance }
      : { y: y - distance });
      break;
    case 'S': Object.assign(newFieldData, resize
      ? { height: height + distance }
      : { y: y + distance });
      break;
    case 'E': Object.assign(newFieldData, resize
      ? { width: width + distance }
      : { x: x + distance });
      break;
    case 'W': Object.assign(newFieldData, resize
      ? { width: width - distance }
      : { x: x - distance });
      break;
    default: return fieldData;
  }

  return newFieldData;
};


const directionMap: Record<Direction, Direction> = {
  N: 'W',
  S: 'E',
  E: 'S',
  W: 'N',
};

export const nudge = <T extends Field>(
  fieldData: T,
  direction: Direction,
  resize: boolean,
): T => {
  let distance = 1;
  if (fieldData.type === FieldTypes.Text && fieldData.signer !== PREPARER) {
    distance = (resize && (direction === 'N' || direction === 'S'))
      // @ts-ignore I don't know why TypeScript isn't figuring out that this is a
      // TextField. I think it has to do with `T extends Field`
      ? fieldData.lineHeight
      : 1;
  }

  let newFieldData = fieldData;
  newFieldData = nudgeImpl(newFieldData, direction, resize, distance);

  if (fieldData.type === FieldTypes.Checkbox || fieldData.type === FieldTypes.Radio) {
    // These have to resize in both directions to remain square.
    if (resize) {
      newFieldData = nudgeImpl(
        newFieldData,
        directionMap[direction],
        resize,
        distance,
      );
    }
  }

  // This envorces things like, checkboxes must always be square, text fields
  // must be a multiple of their lineHeight.
  return applyFieldConstraints(newFieldData);
};

type ResizeParams = {
  editorContext: EditorContextShape,
  zoomContext: ZoomContextShape,
  initialOffset: XYCoord,
  currentOffset: XYCoord,
  item: any,
  fields: Field[],
}
export default ({
  editorContext,
  zoomContext,
  initialOffset: iOffset,
  currentOffset,
  item,
  fields,
}: ResizeParams) => {
  const vertical = (
    item.direction.indexOf('N') >= 0
    || item.direction.indexOf('S') >= 0
  );
  const horizontal = (
    item.direction.indexOf('W') >= 0
    || item.direction.indexOf('E') >= 0
  );

  const resizedFields = fields.map((fieldData) => {
    const { moveDistance } = moveInitialOffset(
      zoomContext,
      item,
      iOffset,
      currentOffset,
    );
    if (!horizontal) {
      moveDistance.x = 0;
    }
    if (!vertical) {
      moveDistance.y = 0;
    }

    // TODO: use signatureRequestContext instead
    // Technically zoomContext can return strings for x, y, width, and height,
    // but that's only for placing fields in the page. It doesn't apply to ORIGIN_VIEWPORT.
    const position: any = zoomContext.toScreenCoords(fieldData, ORIGIN_VIEWPORT);

    let address = zoomContext.fromScreenCoords({
      ...position,
      x: (item.direction.indexOf('W') >= 0
        ? position.x + moveDistance.x
        : position.x
      ),
      y: (item.direction.indexOf('N') >= 0
        ? position.y + moveDistance.y
        : position.y
      ),
      width: (item.direction.indexOf('E') >= 0
        ? position.width + moveDistance.x
        : position.width - moveDistance.x
      ),
      height: (item.direction.indexOf('S') >= 0
        ? position.height + moveDistance.y
        : position.height - moveDistance.y
      ),
    }, ORIGIN_VIEWPORT);

    address = applyFieldConstraints({ ...fieldData, ...address });

    address.width = Math.max(getMinFieldDimensions(fieldData.type, 'width'), address.width);
    address.height = Math.max(getMinFieldDimensions(fieldData.type, 'height'), address.height);

    if (item.direction.indexOf('N') >= 0) {
      // Prevent the bottom from moving
      const bottom = fieldData.y + fieldData.height;
      address.y -= (address.y + address.height) - bottom;
    }
    if (item.direction.indexOf('W') >= 0) {
      // Prevent the right edge from moving
      const right = fieldData.x + fieldData.width;
      address.x -= (address.x + address.width) - right;
    }
    return {
      ...fieldData,
      ...address,
    };
  });

  const valid = resizedFields.reduce(
    (v, field) => v && editorContext.isValidAddress(
      field,
      resizedFields.filter((f) => f !== field),
    ),
    true,
  );


  return { valid, fields: resizedFields };
};
